// n02 Tun Nikohi — paper-invitation preview card built around the
// midnight-nikoh mood. A starlit gold-foil ring frames an inner card on
// deep night-blue paper; a circular medallion holds the couple's photo
// (or a moonlit gradient when missing). Eyebrow reads NIKOH TO'YI,
// names appear in Cormorant Garamond, and the bottom caption pairs the
// date with the venue.
import { Photo, type LayoutCtx } from "./_shared";

// Layered midnight-to-gold gradient — alternating night stops with
// brushed-gold accents read as a foiled stormy-sky frame.
const NIGHT_FOIL =
  "linear-gradient(135deg, #2A2238 0%, #C9A14E 22%, #F0D58E 45%, #C9A14E 65%, #161E33 100%)";

export function NikohPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
}: LayoutCtx) {
  const captionDate = formatDate(date);

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: NIGHT_FOIL,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
          overflow: "hidden",
        }}
      >
        {/* Stars sprinkled across the upper field. */}
        <svg
          aria-hidden
          viewBox="0 0 100 60"
          style={{ position: "absolute", top: "6%", left: "8%", right: "8%", width: "84%", opacity: 0.55 }}
        >
          <circle cx="12" cy="14" r="0.5" fill={ink} />
          <circle cx="28" cy="7" r="0.45" fill={ink} />
          <circle cx="48" cy="18" r="0.6" fill={ink} />
          <circle cx="66" cy="10" r="0.45" fill={ink} />
          <circle cx="82" cy="22" r="0.5" fill={ink} />
          <circle cx="92" cy="12" r="0.4" fill={ink} />
          <circle cx="18" cy="32" r="0.35" fill={accent} opacity="0.85" />
          <circle cx="84" cy="36" r="0.35" fill={accent} opacity="0.85" />
        </svg>

        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
            position: "relative",
            zIndex: 1,
          }}
        >
          NIKOH TO&apos;YI
        </div>

        {/* Circular photo medallion with gold foil ring. */}
        <div
          style={{
            width: "62%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
            zIndex: 1,
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: NIGHT_FOIL,
              padding: "5%",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                background: bg,
                padding: "4%",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <Photo
                  src={photo}
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                  fallback={
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: `radial-gradient(circle at 50% 40%, ${accent}55, ${bg} 65%)`,
                      }}
                    />
                  }
                />
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: `linear-gradient(180deg, transparent 55%, ${accent}22 100%)`,
                  }}
                />
              </div>
            </div>
          </div>
          {[0, 90, 180, 270].map((deg) => (
            <span
              key={deg}
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                width: 4,
                height: 4,
                borderRadius: "50%",
                background: accent,
                transform: `rotate(${deg}deg) translate(0, calc(-50% - 50% + 1px))`,
                transformOrigin: "0 0",
                opacity: 0.85,
              }}
              aria-hidden
            />
          ))}
        </div>

        {/* Names — Cormorant Garamond */}
        <div
          style={{
            fontFamily: font,
            fontSize: 30,
            lineHeight: 1.05,
            fontWeight: 500,
            color: ink,
            letterSpacing: "0.01em",
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        {/* Bottom caption */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
            zIndex: 1,
          }}
        >
          NIKOH MAROSIMI · {captionDate}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
            zIndex: 1,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}

function formatDate(d: string): string {
  const m = d.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) return d.toUpperCase();
  const months = [
    "YANVAR", "FEVRAL", "MART", "APREL", "MAY", "IYUN",
    "IYUL", "AVGUST", "SENTABR", "OKTABR", "NOYABR", "DEKABR",
  ];
  return `${parseInt(m[1], 10)} ${months[parseInt(m[2], 10) - 1] ?? ""} ${m[3]}`;
}
